import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import {
  fetchPublicQuestionnaire,
  saveQuestionnaireResponse,
  submitQuestionnaire,
} from "../../../../services/questionnaireService";
import Loader from "../../../../components/ui/Loader";
import {
  IQuestionnaireResponse,
  IQuestionnaireSaveResponseBody,
  IQuestionnaireSubmitBody,
} from "../../Interfaces/IQuestionnaireInterface";
import Banner from "../../components/Banner/Banner";
import Questionnaire from "../../components/Questionnaire";
import { Modal } from "@react-solutions-ui/findmore-ui";
import NavBar from "../../../../components/layout/NavBar/NavBar";

export type ModalInfo = {
  title: string;
  content: ReactNode;
  closeAction: () => void;
  closeActionLabel: string;
};

export type QuestionnaireResponses = Record<string, string>;

export default function PublicQuestionnaire() {
  const { key } = useParams<{ key: string }>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IQuestionnaireResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [responses, setResponses] = useState<QuestionnaireResponses>({});
  const [completedCategories, setCompletedCategories] = useState<string[]>([]);
  const [savingResponses, setSavingResponses] = useState<boolean>(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [modalInfo, setModalInfo] = useState<ModalInfo | null>(null);

  useEffect(() => {
    if (!key) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchPublicQuestionnaire(key);
        setData(res);
        const answers = Object.fromEntries(
          res.categories.flatMap((category) =>
            category.questions.map((question) => [question.id, question.answer])
          )
        );
        setResponses(answers as QuestionnaireResponses);
        const completed = res.categories
          .filter((category) =>
            category.questions.every(
              (question) => answers[question.id] && answers[question.id] !== ""
            )
          )
          .map((category) => category.categoryName);
        setCompletedCategories(completed);
      } catch (err: any) {
        setError(err?.message || "Erro ao carregar questionário");
      } finally {
        setLoading(false);
      }
    })();
  }, [key]);

  const handleResponseChange = async (
    questionId: string,
    answer: string,
    type: string
  ) => {
    let newResponses = { ...responses };
    if (answer === "") {
      delete newResponses[questionId];
      setResponses(newResponses);
    } else {
      newResponses[questionId] = answer;
      setResponses(newResponses);
    }
    if (type !== "Text") {
      handleSaveResponses(questionId, answer);
    }
    updateCompletedCategories(newResponses, questionId);
  };

  const updateCompletedCategories = (
    newResponses: QuestionnaireResponses,
    questionId: string
  ) => {
    // Get the category name of the question
    const categoryName = data?.categories.find((category) =>
      category.questions.some((question) => question.id === questionId)
    )?.categoryName;
    // Get all the ids of the questions of the category
    const questionsIds =
      data?.categories
        .find((category) =>
          category.questions.some((question) => question.id === questionId)
        )
        ?.questions.map((question) => question.id) || [];
    // Check how many questions are completed
    const completedQuestions = questionsIds.filter(
      (questionId) => newResponses[questionId]
    );
    if (completedQuestions.length === questionsIds.length) {
      setCompletedCategories((prev) => [...prev, categoryName || ""]);
    }
  };

  const handleSaveResponses = async (questionId: string, answer: string) => {
    if (!data || !key) return;
    setSavingResponses(true);
    try {
      const responseBody: IQuestionnaireSaveResponseBody = {
        Key: key,
        Question: {
          QuestionId: questionId,
          Qualification: answer,
        },
      };
      await saveQuestionnaireResponse(responseBody);
      setLastSavedTime(new Date());
    } catch (err: any) {
      setModalInfo({
        title: "Error saving response",
        content: <p>{err?.message || "Error saving response"}</p>,
        closeAction: () => setModalInfo(null),
        closeActionLabel: "Close",
      });
    } finally {
      setSavingResponses(false);
    }
  };

  const handleSubmit = async () => {
    if (!data || !key) return;
    try {
      const submitBody: IQuestionnaireSubmitBody = {
        Key: key,
      };
      await submitQuestionnaire(submitBody);
      setModalInfo({
        title: "Review",
        content: <p>Questionnaire submitted successfully</p>,
        closeAction: () => {
          setModalInfo(null);
          //Refresh the page
          window.location.reload();
        },
        closeActionLabel: "Review",
      });
    } catch (err: any) {
      setModalInfo({
        title: "Error submitting questionnaire",
        content: (
          <p>{err.response.data.error || "Error submitting questionnaire"}</p>
        ),
        closeAction: () => setModalInfo(null),
        closeActionLabel: "Close",
      });
    }
  };

  if (!key) return <div style={{ padding: 24 }}>Key missing in route.</div>;
  if (loading) return <Loader text="Loading questionnaire..." />;
  if (error)
    return <div style={{ padding: 24, color: "red" }}>Error: {error}</div>;
  if (!data) return <div style={{ padding: 24 }}>No data received.</div>;

  return (
    <>
      <NavBar items={[]} />
      <div
        style={{
          padding: "var(--navbar-height, 112px) 0 24px 0",
          boxSizing: "border-box",
        }}
      >
        <Banner
          evaluatorType={data.evaluatorType}
          autoEvaluation={data.autoEvaluation}
          evaluatedName={data.evaluatedName}
        />

        <Questionnaire
          data={data.categories}
          responses={responses}
          onResponseChange={handleResponseChange}
          onSubmit={handleSubmit}
          onSaveResponses={handleSaveResponses}
          completedCategories={completedCategories}
          autoEvaluation={data.autoEvaluation}
          evaluatedName={data.evaluatedName}
          savingResponses={savingResponses}
          lastSavedTime={lastSavedTime}
          autoCompleted={data.autoCompleted}
          evaluatorType={data.evaluatorType}
          evaluationId={data.evaluationId}
        />
      </div>
      {modalInfo && (
        <Modal
          title={modalInfo?.title!}
          type="info"
          onCloseModal={modalInfo?.closeAction!}
          closeModalLabel={modalInfo?.closeActionLabel!}
        >
          {modalInfo?.content!}
        </Modal>
      )}
    </>
  );
}
